import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FileText, 
  Calendar, 
  Download, 
  PlusCircle, 
  X, 
  CheckCircle, 
  AlertCircle,
  Loader2,
  BarChart3,
  Clock,
  Mail,
  RefreshCw,
  ChevronDown,
  MessageSquare,
  Zap,
  Filter,
  Hash,
  User,
  ChevronLeft,
  ChevronRight,
  Search,
  Code
} from 'lucide-react';
import Config from '../../utils/Config';
import CustomerLayout from '../../layouts/CustomerLayout';
import senderRequests from '../../api/Customer/SenderRequests';

const BASE_URL_LIVE = `${Config.baseUrl}api`;

const MessageReports = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [exporting, setExporting] = useState(new Set());
  const [showGenerateForm, setShowGenerateForm] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [showMessageTypeDropdown, setShowMessageTypeDropdown] = useState(false);
  const [showSenderDropdown, setShowSenderDropdown] = useState(false);
  
  // Sender ID state with pagination
  const [senders, setSenders] = useState([]);
  const [loadingSenders, setLoadingSenders] = useState(false);
  const [senderSearchTerm, setSenderSearchTerm] = useState('');
  const [senderPagination, setSenderPagination] = useState({
    currentPage: 1,
    perPage: 10,
    total: 0,
    lastPage: 1,
    from: 0,
    to: 0,
  });
  
  // Reports pagination state
  const [reportsPagination, setReportsPagination] = useState({
    currentPage: 1,
    perPage: 10,
    total: 0,
    lastPage: 1,
    from: 0,
    to: 0,
  });
  const [reportsSearchTerm, setReportsSearchTerm] = useState('');
  const [debouncedReportsSearch, setDebouncedReportsSearch] = useState('');

  const user = localStorage.getItem("txtconnect_ng_user");
  const userObject = user ? JSON.parse(user) : null;

  // Form state
  const [formData, setFormData] = useState({
    start_date: '',
    end_date: '',
    report_name: '',
    report_type: 'bulk',
    user_id: userObject?.txtconnect_ng_user?.id || '',
    sender: '',
  });

  // Message types
  const messageTypes = [
    {
      value: 'bulk',
      label: 'Bulk Messages',
      description: 'Scheduled and mass messages sent to multiple recipients',
      icon: MessageSquare,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50'
    },
    {
      value: 'quick',
      label: 'Quick Messages',
      description: 'Instant messages sent to individual or small groups',
      icon: MessageSquare,
      color: 'text-green-600',
      bgColor: 'bg-green-50'
    },
    {
      value: 'api',
      label: 'API Messages',
      description: 'Messages sent programmatically through the API integration',
      icon: Code,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50'
    },
    {
      value: 'all',
      label: 'All Messages',
      description: 'Bulk, quick and API messages combined in one report',
      icon: Filter,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50'
    }
  ];

  const selectedType = messageTypes.find(type => type.value === formData.report_type);

  // Get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem("txtconnect_ng_user_token");
    return {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      }
    };
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = String(date.getFullYear()).slice(-2);
      return `${day}/${month}/${year}`;
    } catch (error) {
      return 'Invalid date';
    }
  };

  // Fetch senders with pagination - UPDATED TO HANDLE NEW API STRUCTURE
 const fetchSenders = useCallback(async (page = 1, search = '') => {
  setLoadingSenders(true);
  try {
    // Pass page, perPage, and search to the API
    const response = await senderRequests.getSenders(
      page, 
      senderPagination.perPage, 
      search
    );
    
    if (response.success && response.data?.data?.status_code === "000") {
      const apiData = response.data?.data?.data;
      
      if (!apiData || !apiData.data) {
        showToast('No sender data available', 'error');
        setSenders([]);
        return;
      }
      
      const sendersData = apiData.data || [];
      const apiPagination = apiData.pagination;
      
      setSenders(sendersData);
      //comment
      if (apiPagination) {
        setSenderPagination(prev => ({
          ...prev,
          currentPage: apiPagination.current_page || page,
          perPage: apiPagination.per_page || senderPagination.perPage,
          total: apiPagination.total || 0,
          lastPage: apiPagination.last_page || 1,
          from: apiPagination.from || 0,
          to: apiPagination.to || 0
        }));
      } else {
        // Fallback to client-side pagination if no API pagination
        const total = sendersData.length;
        const perPage = senderPagination.perPage;
        const lastPage = Math.ceil(total / perPage);
        const startIndex = (page - 1) * perPage;
        const endIndex = startIndex + perPage;
        const paginatedSenders = sendersData.slice(startIndex, endIndex);
        
        setSenders(paginatedSenders);
        setSenderPagination(prev => ({
          ...prev,
          currentPage: page,
          total,
          lastPage,
          from: startIndex + 1,
          to: Math.min(endIndex, total)
        }));
      }
    } else {
      showToast('Failed to fetch sender IDs', 'error');
      setSenders([]);
    }
  } catch (error) {
    console.error('Error fetching senders:', error);
    showToast('Error fetching sender IDs', 'error');
    setSenders([]);
  } finally {
    setLoadingSenders(false);
  }
}, [senderPagination.perPage]);

  // Fetch senders with server-side pagination
  // Fetch senders with server-side pagination
const fetchSendersServerSide = useCallback(async (page = 1, search = '') => {
  setLoadingSenders(true);
  try {
    const response = await senderRequests.getSenders(page, senderPagination.perPage, search);
    
    if (response.success && response.data?.data?.status_code === "000") {
      const apiData = response.data?.data?.data;
      
      if (!apiData || !apiData.data) {
        setSenders([]);
        return;
      }
      
      const sendersData = apiData.data || [];
      const apiPagination = apiData.pagination;
      
      setSenders(sendersData);
      
      if (apiPagination) {
        setSenderPagination(prev => ({
          ...prev,
          currentPage: apiPagination.current_page || page,
          perPage: apiPagination.per_page || prev.perPage,
          total: apiPagination.total || 0,
          lastPage: apiPagination.last_page || 1,
          from: apiPagination.from || 0,
          to: apiPagination.to || 0
        }));
      }
    } else {
      showToast('Failed to fetch sender IDs', 'error');
      setSenders([]);
    }
  } catch (error) {
    console.error('Error fetching senders:', error);
    showToast('Error fetching sender IDs', 'error');
    setSenders([]);
  } finally {
    setLoadingSenders(false);
  }
}, [senderPagination.perPage]);

  // Debounced sender search
  // useEffect(() => {
  //   const debounceTimer = setTimeout(() => {
  //     // Choose which fetch function to use based on your API
  //     fetchSenders(1, senderSearchTerm);
  //     // OR use server-side pagination if your API supports it:
  //     // fetchSendersServerSide(1, senderSearchTerm);
  //   }, 500);

  //   return () => clearTimeout(debounceTimer);
  // }, [senderSearchTerm, fetchSenders]);

  // Debounced sender search
useEffect(() => {
  const debounceTimer = setTimeout(() => {
    // Use server-side pagination with search
    fetchSenders(1, senderSearchTerm);
  }, 500);

  return () => clearTimeout(debounceTimer);
}, [senderSearchTerm, fetchSenders]);

  // Fetch reports with pagination
  const fetchReports = useCallback(async (page = 1, search = '') => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${BASE_URL_LIVE}/user/show/report`,
        getAuthHeaders()
      );
      
      if (response.data?.data?.status_code === "000") {
        const reportsData = response.data.data.data?.message_reports || [];
        
        // Apply search filter
        let filteredReports = reportsData;
        if (search.trim()) {
          const searchLower = search.toLowerCase();
          filteredReports = reportsData.filter(
            (report) =>
              report.report_name?.toLowerCase().includes(searchLower) ||
              report.report_type?.toLowerCase().includes(searchLower) ||
              report.sender_id?.toString().includes(search)
          );
        }
        
        // Calculate pagination
        const total = filteredReports.length;
        const perPage = reportsPagination.perPage;
        const lastPage = Math.ceil(total / perPage);
        const startIndex = (page - 1) * perPage;
        const endIndex = startIndex + perPage;
        const paginatedReports = filteredReports.slice(startIndex, endIndex);
        
        setReports(paginatedReports);
        setReportsPagination(prev => ({
          ...prev,
          currentPage: page,
          total,
          lastPage,
          from: startIndex + 1,
          to: Math.min(endIndex, total)
        }));
      } else {
        showToast('Failed to fetch reports', 'error');
        setReports([]);
      }
    } catch (error) {
      console.error('Error fetching reports:', error);
      showToast('Error fetching reports', 'error');
      setReports([]);
    } finally {
      setLoading(false);
    }
  }, [reportsPagination.perPage]);

  // Debounced reports search
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      setDebouncedReportsSearch(reportsSearchTerm);
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [reportsSearchTerm]);

  // Fetch reports when search term changes
  useEffect(() => {
    fetchReports(1, debouncedReportsSearch);
  }, [debouncedReportsSearch, fetchReports]);

  // Generate report
  const handleGenerateReport = async (e) => {
    e.preventDefault();
    setGenerating(true);
    
    const payload = {
    start_date: formData.start_date,
    end_date: formData.end_date,
    report_name: formData.report_name,
    report_type: formData.report_type || 'bulk',
    user_id: formData.user_id || userObject?.txtconnect_ng_user?.id,
    ...(formData.sender && { sender_id: formData.sender }), // Changed to sender (name)
  };
    
    try {
      const response = await axios.post(
        `${BASE_URL_LIVE}/user/generate/report`,
        payload,
        getAuthHeaders()
      );
      
      if (response.data?.data?.status_code === "000") {
        showToast('Report generated successfully!', 'success');
        setShowGenerateForm(false);
        setFormData({
          start_date: '',
          end_date: '',
          report_name: '',
          report_type: 'bulk',
          user_id: userObject?.txtconnect_ng_user?.id || '',
          sender_id: '',
        });
        fetchReports(reportsPagination.currentPage, debouncedReportsSearch);
      } else {
        showToast('Failed to generate report', 'error');
      }
    } catch (error) {
      console.error('Error generating report:', error);
      showToast(error.response?.data?.data?.reason || error.message || 'Error generating report', 'error');
    } finally {
      setGenerating(false);
    }
  };

  // Export report
  const handleExportReport = async (reportId, reportName) => {
    setExporting(prev => new Set(prev).add(reportId));
    try {
      const response = await axios.post(
        `${BASE_URL_LIVE}/user/message-reports/export-email`,
        { report_id: reportId },
        getAuthHeaders()
      );
      
      if (response.data?.data?.status_code === "000") {
        showToast(`Report "${reportName}" exported successfully! Please check your email.`, 'success');
      } else {
        showToast('Failed to export report', 'error');
      }
    } catch (error) {
      console.error('Error exporting report:', error);
      showToast('Error exporting report', 'error');
    } finally {
      setExporting(prev => {
        const newSet = new Set(prev);
        newSet.delete(reportId);
        return newSet;
      });
    }
  };

  // Show toast notification
  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: '', type: 'success' });
    }, 5000);
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle message type selection
  const handleMessageTypeSelect = (typeValue) => {
    setFormData(prev => ({
      ...prev,
      report_type: typeValue
    }));
    setShowMessageTypeDropdown(false);
  };

 // Handle sender selection
const handleSenderSelect = (senderId, senderName = '') => {
  setFormData(prev => {
    let newReportName = prev.report_name;
    const selectedType = messageTypes.find(type => type.value === prev.report_type);
    
    if (!prev.report_name && selectedType) {
      const today = new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      const senderSuffix = senderName ? ` for ${senderName}` : '';
      newReportName = `${selectedType.label} ${today}${senderSuffix}`;
    }
    
    return {
      ...prev,
      sender: senderName, // Store sender name instead of ID
      report_name: newReportName
    };
  });
  setShowSenderDropdown(false);
};

  // Clear sender selection
  const handleClearSender = () => {
    setFormData(prev => ({
      ...prev,
      sender_id: ''
    }));
  };

  // Handle sender page change - UPDATED FOR SERVER-SIDE PAGINATION
  const handleSenderPageChange = (page) => {
    // If using client-side pagination
    // fetchSenders(page, senderSearchTerm);
    
    // OR if using server-side pagination:
    fetchSendersServerSide(page, senderSearchTerm);
  };

  // Handle sender search change
  const handleSenderSearchChange = (e) => {
    setSenderSearchTerm(e.target.value);
    setSenderPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  // Handle reports page change
  const handleReportsPageChange = (page) => {
    setReportsPagination(prev => ({ ...prev, currentPage: page }));
    fetchReports(page, debouncedReportsSearch);
  };

  // Handle reports per page change
  const handleReportsPerPageChange = (perPage) => {
    setReportsPagination(prev => ({ 
      ...prev, 
      perPage: parseInt(perPage),
      currentPage: 1 
    }));
  };

  // Handle sender per page change
 // Handle sender per page change
const handleSenderPerPageChange = (perPage) => {
  setSenderPagination(prev => ({ 
    ...prev, 
    perPage: parseInt(perPage),
    currentPage: 1 
  }));
  // Refetch with new page size and current search term
  fetchSenders(1, senderSearchTerm);
};

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showMessageTypeDropdown && !event.target.closest('.message-type-dropdown')) {
        setShowMessageTypeDropdown(false);
      }
      if (showSenderDropdown && !event.target.closest('.sender-dropdown')) {
        setShowSenderDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMessageTypeDropdown, showSenderDropdown]);

  // Fetch initial data
  useEffect(() => {
    fetchReports();
    fetchSenders();
  }, []);

  // Pagination component
  const Pagination = ({ pagination, onPageChange, onPerPageChange }) => {
    const { currentPage, lastPage, perPage, from, to, total } = pagination;
    
    const getPageNumbers = () => {
      const pages = [];
      const maxVisible = 5;
      
      if (lastPage <= maxVisible) {
        for (let i = 1; i <= lastPage; i++) pages.push(i);
      } else {
        if (currentPage <= 3) {
          for (let i = 1; i <= 4; i++) pages.push(i);
          pages.push('...');
          pages.push(lastPage);
        } else if (currentPage >= lastPage - 2) {
          pages.push(1);
          pages.push('...');
          for (let i = lastPage - 3; i <= lastPage; i++) pages.push(i);
        } else {
          pages.push(1);
          pages.push('...');
          for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
          pages.push('...');
          pages.push(lastPage);
        }
      }
      
      return pages;
    };

    return (
      <div className="flex flex-col gap-4 justify-between items-center px-6 py-4 border-t border-gray-200 bg-gray-50 sm:flex-row">
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-700">Show</span>
          <select
            value={perPage}
            onChange={(e) => onPerPageChange(e.target.value)}
            className="px-2 py-1 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={15}>15</option>
            <option value={20}>20</option>
          </select>
          <span className="text-sm text-gray-700">entries</span>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-4 h-4" />
            <span className="hidden sm:inline ml-1">Previous</span>
          </button>

          <div className="flex gap-1 items-center">
            {getPageNumbers().map((page, index) => (
              page === '...' ? (
                <span key={`ellipsis-${index}`} className="px-3 py-1 text-sm text-gray-400">
                  ...
                </span>
              ) : (
                <button
                  key={page}
                  onClick={() => onPageChange(page)}
                  className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                    currentPage === page
                      ? 'bg-red-600 text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {page}
                </button>
              )
            ))}
          </div>

          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === lastPage}
            className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span className="hidden sm:inline mr-1">Next</span>
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        <div className="text-sm text-gray-700">
          Showing {from} to {to} of {total} entries
        </div>
      </div>
    );
  };

  return (
    <CustomerLayout>
      <div className="min-h-screen mt-16 bg-[#f8f8f8] p-4 md:p-6">
        {/* Toast Notification */}
        <AnimatePresence>
          {toast.show && (
            <motion.div
              initial={{ opacity: 0, y: -50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -50 }}
              className={`fixed top-20 right-4 z-50 p-4 rounded-lg shadow-lg flex items-center space-x-3 ${
                toast.type === 'success' 
                  ? 'bg-green-50 text-green-800 border border-green-200' 
                  : 'bg-red-50 text-red-800 border border-red-200'
              }`}
            >
              {toast.type === 'success' ? (
                <CheckCircle className="h-5 w-5 text-green-600" />
              ) : (
                <AlertCircle className="h-5 w-5 text-red-600" />
              )}
              <p className="font-medium">{toast.message}</p>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-8"
          >
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
              <div className="flex items-center space-x-4">
                <div className="p-3 rounded-xl bg-red-50">
                  <FileText className="h-8 w-8 text-red-600" />
                </div>
                <div>
                  <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
                    Message Reports
                  </h1>
                  <p className="text-gray-600 mt-1">
                    Manage and generate message reports for your account
                  </p>
                </div>
              </div>
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <button
                  onClick={() => setShowGenerateForm(!showGenerateForm)}
                  className={`flex items-center space-x-2 px-5 py-3 rounded-xl font-semibold shadow-sm transition-all duration-300 ${
                    showGenerateForm
                      ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      : 'bg-red-600 text-white hover:bg-red-700'
                  }`}
                >
                  {showGenerateForm ? (
                    <>
                      <X className="h-5 w-5" />
                      <span>Cancel</span>
                    </>
                  ) : (
                    <>
                      <PlusCircle className="h-5 w-5" />
                      <span>Generate New Report</span>
                    </>
                  )}
                </button>
              </motion.div>
            </div>
          </motion.div>

          {/* Generate Report Form */}
          <AnimatePresence>
            {showGenerateForm && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mb-8 overflow-hidden"
              >
                <div className="bg-gray-50 rounded-2xl border border-gray-200 p-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center space-x-2">
                    <PlusCircle className="h-5 w-5 text-red-600" />
                    <span>Generate New Report</span>
                  </h2>
                  <form onSubmit={handleGenerateReport}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                      <div>
                        <label className="block text-gray-700 font-medium mb-2 flex items-center space-x-2">
                          <Calendar className="h-4 w-4 text-gray-500" />
                          <span>Start Date</span>
                        </label>
                        <input
                          type="date"
                          name="start_date"
                          value={formData.start_date}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-300"
                        />
                      </div>
                      <div>
                        <label className="block text-gray-700 font-medium mb-2 flex items-center space-x-2">
                          <Calendar className="h-4 w-4 text-gray-500" />
                          <span>End Date</span>
                        </label>
                        <input
                          type="date"
                          name="end_date"
                          value={formData.end_date}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-300"
                        />
                      </div>

                      {/* Message Type Dropdown */}
                      <div className="md:col-span-2 message-type-dropdown">
                        <label className="block text-gray-700 font-medium mb-2 flex items-center space-x-2">
                          <FileText className="h-4 w-4 text-gray-500" />
                          <span>Message Type *</span>
                        </label>
                        <div className="relative">
                          <button
                            type="button"
                            onClick={() => setShowMessageTypeDropdown(!showMessageTypeDropdown)}
                            className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-300 flex items-center justify-between hover:bg-gray-50"
                          >
                            <div className="flex items-center space-x-3">
                              {selectedType && (
                                <>
                                  <div className={`p-2 rounded-lg ${selectedType.bgColor}`}>
                                    <selectedType.icon className={`h-5 w-5 ${selectedType.color}`} />
                                  </div>
                                  <div className="text-left">
                                    <div className="font-medium text-gray-900">
                                      {selectedType.label}
                                    </div>
                                    <div className="text-sm text-gray-500">
                                      {selectedType.description}
                                    </div>
                                  </div>
                                </>
                              )}
                            </div>
                            <ChevronDown className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${showMessageTypeDropdown ? 'rotate-180' : ''}`} />
                          </button>

                          <AnimatePresence>
                            {showMessageTypeDropdown && (
                              <motion.div
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden"
                              >
                                <div className="py-1">
                                  {messageTypes.map((type) => {
                                    const Icon = type.icon;
                                    return (
                                      <button
                                        key={type.value}
                                        type="button"
                                        onClick={() => handleMessageTypeSelect(type.value)}
                                        className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors duration-150 flex items-center space-x-3 ${
                                          formData.report_type === type.value ? 'bg-blue-50' : ''
                                        }`}
                                      >
                                        <div className={`p-2 rounded-lg ${type.bgColor}`}>
                                          <Icon className={`h-5 w-5 ${type.color}`} />
                                        </div>
                                        <div className="flex-1 text-left">
                                          <div className="font-medium text-gray-900">
                                            {type.label}
                                          </div>
                                          <div className="text-sm text-gray-500 mt-0.5">
                                            {type.description}
                                          </div>
                                        </div>
                                        {formData.report_type === type.value && (
                                          <CheckCircle className="h-5 w-5 text-green-500" />
                                        )}
                                      </button>
                                    );
                                  })}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      </div>

                      {/* Sender ID Dropdown with Pagination */}
                      <div className="md:col-span-2 sender-dropdown">
                        <label className="block text-gray-700 font-medium mb-2 flex items-center space-x-2">
                          <Filter className="h-4 w-4 text-gray-500" />
                          <span>Filter by Sender ID (Optional)</span>
                        </label>
                        <div className="relative">
                          <div className="relative">
                            <button
                              type="button"
                              onClick={() => setShowSenderDropdown(!showSenderDropdown)}
                              className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-300 flex items-center justify-between hover:bg-gray-50"
                            >
                              <div className="flex items-center space-x-3">
                                {formData.sender ? (
                                  <>
                                    <div className="p-2 rounded-lg bg-purple-50">
                                      <Hash className="h-5 w-5 text-purple-600" />
                                    </div>
                                    <div className="text-left">
                                      <div className="font-medium text-gray-900">
                                        {senders.find(s => s.id.toString() === formData.sender_id)?.sender || 'Selected Sender'}
                                      </div>
                                      <div className="text-sm text-gray-500">
                                        Sender ID: {formData.sender}
                                      </div>
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    <div className="p-2 rounded-lg bg-gray-50">
                                      <User className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <div className="text-left">
                                      <div className="font-medium text-gray-900">
                                        Select a Sender ID (Optional)
                                      </div>
                                      <div className="text-sm text-gray-500">
                                        Filter reports by specific sender
                                      </div>
                                    </div>
                                  </>
                                )}
                              </div>
                              <div className="flex items-center space-x-2">
                                {formData.sender_id && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleClearSender();
                                    }}
                                    className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors duration-150"
                                  >
                                    <X className="h-4 w-4" />
                                  </button>
                                )}
                                <ChevronDown className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${showSenderDropdown ? 'rotate-180' : ''}`} />
                              </div>
                            </button>
                          </div>

                          <AnimatePresence>
                            {showSenderDropdown && (
                              <motion.div
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden max-h-96"
                              >
                                <div className="p-3 border-b border-gray-200">
                                  <div className="relative">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                    <input
                                      type="text"
                                      placeholder="Search sender IDs..."
                                      value={senderSearchTerm}
                                      onChange={handleSenderSearchChange}
                                      className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                    />
                                  </div>
                                </div>

                                <div className="max-h-64 overflow-y-auto">
                                  <div className="py-1">
                                    <button
                                      type="button"
                                      onClick={() => handleSenderSelect('')}
                                      className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors duration-150 flex items-center space-x-3 ${
                                        formData.sender_id === '' ? 'bg-blue-50' : ''
                                      }`}
                                    >
                                      <div className="p-2 rounded-lg bg-gray-50">
                                        <User className="h-5 w-5 text-gray-600" />
                                      </div>
                                      <div className="flex-1 text-left">
                                        <div className="font-medium text-gray-900">
                                          All Senders
                                        </div>
                                        <div className="text-sm text-gray-500">
                                          Include messages from all sender IDs
                                        </div>
                                      </div>
                                      {formData.sender_id === '' && (
                                        <CheckCircle className="h-5 w-5 text-green-500" />
                                      )}
                                    </button>

                                    {loadingSenders && (
                                      <div className="px-4 py-8 flex flex-col items-center justify-center">
                                        <Loader2 className="h-8 w-8 text-gray-400 animate-spin mb-2" />
                                        <span className="text-sm text-gray-500">Loading senders...</span>
                                      </div>
                                    )}

                                    {!loadingSenders && senders.length === 0 && (
                                      <div className="px-4 py-8 text-center">
                                        <User className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                                        <p className="text-sm text-gray-500">No sender IDs found</p>
                                      </div>
                                    )}

                                    {!loadingSenders && senders.length > 0 && (
                                      <>
                                        <div className="px-4 py-2 border-t border-gray-200">
                                          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                            Your Sender IDs ({senderPagination.total})
                                          </div>
                                        </div>
                                        {senders.map((sender) => (
                                           <button
    key={sender.id}
    type="button"
    onClick={() => handleSenderSelect(sender.id, sender.sender)} // Pass sender.sender (name)
    className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors duration-150 flex items-center space-x-3 ${
      formData.sender === sender.sender ? 'bg-blue-50' : '' // Compare with sender name
    }`}
  >
                                            <div className="p-2 rounded-lg bg-purple-50">
                                              <Hash className="h-5 w-5 text-purple-600" />
                                            </div>
                                            <div className="flex-1 text-left">
                                              <div className="font-medium text-gray-900">
                                                {sender.sender || 'Unnamed Sender'}
                                              </div>
                                              <div className="text-sm text-gray-500">
                                                ID: {sender.id}
                                                <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${
                                                  sender.status === 'Approved' || sender.status === 'approved' 
                                                    ? 'bg-green-100 text-green-800' 
                                                    : sender.status === 'Pending' || sender.status === 'pending'
                                                    ? 'bg-yellow-100 text-yellow-800'
                                                    : sender.status === 'Rejected' || sender.status === 'rejected'
                                                    ? 'bg-red-100 text-red-800'
                                                    : 'bg-gray-100 text-gray-800'
                                                }`}>
                                                  {sender.status}
                                                </span>
                                              </div>
                                            </div>
                                            {formData.sender_id === sender.id.toString() && (
                                              <CheckCircle className="h-5 w-5 text-green-500" />
                                            )}
                                          </button>
                                        ))}
                                      </>
                                    )}
                                  </div>
                                </div>

                                {/* Sender Pagination */}
                                {senderPagination.total > 0 && (
                                  <div className="px-4 py-3 border-t border-gray-200 bg-gray-50">
                                    <Pagination 
                                      pagination={senderPagination} 
                                      onPageChange={handleSenderPageChange}
                                      onPerPageChange={handleSenderPerPageChange}
                                    />
                                  </div>
                                )}
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                        <p className="mt-1 text-sm text-gray-500">
                          Select a specific sender ID to filter messages, or leave empty to include all senders
                        </p>
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-gray-700 font-medium mb-2 flex items-center space-x-2">
                          <FileText className="h-4 w-4 text-gray-500" />
                          <span>Report Name *</span>
                        </label>
                        <input
                          type="text"
                          name="report_name"
                          value={formData.report_name}
                          onChange={handleInputChange}
                          placeholder={`e.g., ${formData.report_type === 'bulk' ? 'January Bulk Messages' : formData.report_type === 'api' ? 'API Messages' : formData.report_type === 'all' ? 'All Messages' : 'Quick Messages Q1'} Report${formData.sender_id ? ` for ${senders.find(s => s.id.toString() === formData.sender_id)?.sender || 'Selected Sender'}` : ''}`}
                          required
                          className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-300"
                        />
                        <div className="mt-1 text-sm text-gray-500">
                          {formData.report_type === 'bulk' 
                            ? 'Include date range and purpose for bulk messages'
                            : formData.report_type === 'api'
                              ? 'Describe the API messages report purpose'
                              : formData.report_type === 'all'
                                ? 'Covers bulk, quick and API messages in the date range'
                                : 'Describe the quick messages report purpose'
                          }
                          {formData.sender_id && (
                            <span className="block mt-1 text-purple-600">
                              Includes messages only from selected sender
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-3">
                      <button
                        type="button"
                        onClick={() => setShowGenerateForm(false)}
                        className="px-5 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-all duration-300"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={generating}
                        className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-medium transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                      >
                        {generating ? (
                          <>
                            <Loader2 className="h-5 w-5 animate-spin" />
                            <span>Generating...</span>
                          </>
                        ) : (
                          <>
                            <PlusCircle className="h-5 w-5" />
                            <span>
                              Generate {selectedType?.label} Report
                              {formData.sender_id && ' (Filtered)'}
                            </span>
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Reports Table with Pagination */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden"
          >
            <div className="px-6 py-5 border-b border-gray-200 bg-gray-50">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 className="text-xl font-bold text-gray-900 flex items-center space-x-2">
                    <BarChart3 className="h-5 w-5 text-red-600" />
                    <span>Generated Reports</span>
                  </h2>
                  <p className="text-gray-600 mt-1">
                    All your generated message reports are listed below
                  </p>
                </div>
                <div className="flex gap-3 items-center">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Search reports..."
                      value={reportsSearchTerm}
                      onChange={(e) => setReportsSearchTerm(e.target.value)}
                      className="pl-10 pr-4 py-2 w-full sm:w-64 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                    />
                  </div>
                  <button
                    onClick={() => {
                      fetchReports(reportsPagination.currentPage, debouncedReportsSearch);
                      fetchSenders(senderPagination.currentPage, senderSearchTerm);
                    }}
                    disabled={loading}
                    className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-300"
                  >
                    <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
                  </button>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="p-12 text-center">
                <Loader2 className="h-12 w-12 text-red-600 mx-auto animate-spin" />
                <p className="mt-4 text-gray-600 font-medium">Loading reports...</p>
              </div>
            ) : !Array.isArray(reports) ? (
              <div className="p-12 text-center">
                <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                  <AlertCircle className="h-8 w-8 text-red-600" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Reports</h3>
                <p className="text-gray-600 mb-6">Unable to load reports data. Please try again.</p>
                <button
                  onClick={() => fetchReports()}
                  className="bg-red-600 hover:bg-red-700 text-white px-5 py-2.5 rounded-lg font-medium inline-flex items-center space-x-2"
                >
                  <RefreshCw className="h-4 w-4" />
                  <span>Retry</span>
                </button>
              </div>
            ) : reports.length === 0 ? (
              <div className="p-12 text-center">
                <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  <FileText className="h-8 w-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No reports yet</h3>
                <p className="text-gray-600 mb-6">Create your first report to get started</p>
                <button
                  onClick={() => setShowGenerateForm(true)}
                  className="bg-red-600 hover:bg-red-700 text-white px-5 py-2.5 rounded-lg font-medium inline-flex items-center space-x-2"
                >
                  <PlusCircle className="h-4 w-4" />
                  <span>Generate Report</span>
                </button>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider">
                          Report Name
                        </th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider">
                          Type
                        </th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider">
                          Sender ID
                        </th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider">
                          Start Date
                        </th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider">
                          End Date
                        </th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider">
                          Created At
                        </th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {reports.map((report, index) => {
                        const reportType = messageTypes.find(type => type.value === report.report_type);
                        return (
                          <motion.tr
                            key={report.id || index}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.3, delay: index * 0.05 }}
                            className="hover:bg-gray-50 transition-colors duration-150"
                          >
                            <td className="px-6 py-4">
                              <div className="flex items-center space-x-3">
                                <div className={`p-2 rounded-lg ${reportType?.bgColor || 'bg-gray-50'}`}>
                                  {reportType ? (
                                    <reportType.icon className={`h-5 w-5 ${reportType.color}`} />
                                  ) : (
                                    <FileText className="h-5 w-5 text-gray-600" />
                                  )}
                                </div>
                                <div>
                                  <div className="font-medium text-gray-900">
                                    {report.report_name || 'Unnamed Report'}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    ID: {report.id || 'N/A'}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex items-center space-x-2">
                                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium capitalize ${reportType?.bgColor || 'bg-gray-100'} ${reportType?.color || 'text-gray-800'}`}>
                                  {reportType?.icon && <reportType.icon className="h-3 w-3 mr-1" />}
                                  {report.report_type || 'unknown'}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm text-gray-700">
                                {report.sender_id ? (
                                  <div className="flex items-center space-x-2">
                                    <Hash className="h-4 w-4 text-purple-500" />
                                    <span className="font-medium">{report.sender_id}</span>
                                  </div>
                                ) : (
                                  <div className="flex items-center space-x-2">
                                    <User className="h-4 w-4 text-gray-400" />
                                    <span className="text-gray-500">All Senders</span>
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm text-gray-700 flex items-center space-x-2">
                                <Calendar className="h-4 w-4 text-gray-400" />
                                <span>{formatDate(report.start_date)}</span>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm text-gray-700 flex items-center space-x-2">
                                <Calendar className="h-4 w-4 text-gray-400" />
                                <span>{formatDate(report.end_date)}</span>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm text-gray-700 flex items-center space-x-2">
                                <Clock className="h-4 w-4 text-gray-400" />
                                <span>{formatDate(report.created_at)}</span>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <button
                                onClick={() => handleExportReport(report.id, report.report_name)}
                                disabled={exporting.has(report.id)}
                                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                              >
                                {exporting.has(report.id) ? (
                                  <>
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    <span>Exporting...</span>
                                  </>
                                ) : (
                                  <>
                                    <Mail className="h-4 w-4" />
                                    <span>Email Export</span>
                                  </>
                                )}
                              </button>
                            </td>
                          </motion.tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Reports Pagination */}
                {reportsPagination.total > reportsPagination.perPage && (
                  <Pagination 
                    pagination={reportsPagination} 
                    onPageChange={handleReportsPageChange}
                    onPerPageChange={handleReportsPerPageChange}
                  />
                )}
              </>
            )}
          </motion.div>

          {/* Stats Cards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-2xl font-bold text-gray-900">
                    {reportsPagination.total}
                  </div>
                  <div className="text-gray-600 mt-1">Total Reports</div>
                </div>
                <div className="p-3 bg-red-50 rounded-lg">
                  <FileText className="h-6 w-6 text-red-600" />
                </div>
              </div>
            </div>
            
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-2xl font-bold text-gray-900">
                    {reports.filter(r => r.report_type === 'bulk').length}
                  </div>
                  <div className="text-gray-600 mt-1">Bulk Reports</div>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg">
                  <MessageSquare className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </div>
            
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-2xl font-bold text-gray-900">
                    {senderPagination.total}
                  </div>
                  <div className="text-gray-600 mt-1">Available Senders</div>
                </div>
                <div className="p-3 bg-purple-50 rounded-lg">
                  <Hash className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </div>
            
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-2xl font-bold text-gray-900">
                    {new Date().toLocaleDateString()}
                  </div>
                  <div className="text-gray-600 mt-1">Last Updated</div>
                </div>
                <div className="p-3 bg-purple-50 rounded-lg">
                  <Clock className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </CustomerLayout>
  );
};

export default MessageReports;